import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import {
  Notification,
  NotificationsService,
  type NotificationTab,
} from '@layout/data/notifications';
import { NotificationsStreamService } from '@layout/data/notifications-stream';
import { FinanceInvoiceService } from '@shared/data/finance-invoice.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  BuiPopover,
  BuiPopoverClose,
  BuiPopoverContent,
  BuiPopoverPortal,
  BuiPopoverTrigger,
  PopoverPosition,
} from '@ui/popover';
import { BuiSkeleton, BuiSkeletonRepeat } from '@ui/skeleton';

@Component({
  selector: 'notifications',
  imports: [
    MatButton,
    MatIconButton,
    MatIcon,
    MatBadge,
    MatDivider,
    MatTab,
    MatTabGroup,
    MatTooltip,
    MatProgressSpinner,
    TranslocoDirective,
    TranslocoDatePipe,
    BuiPopover,
    BuiPopoverTrigger,
    BuiPopoverPortal,
    BuiPopoverContent,
    BuiPopoverClose,
    BuiSkeleton,
    BuiSkeletonRepeat,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t">
      <div
        buiPopover
        class="w-full sm:max-w-sm"
        [position]="position()"
        [closeOnScroll]="true"
        (opened)="onOpened()"
      >
        <!-- Trigger -->
        <button
          matIconButton
          buiPopoverTrigger
          [matBadge]="unreadTotal()"
          [matBadgeHidden]="!unreadTotal()"
          matBadgeSize="small"
        >
          <mat-icon [svgIcon]="unreadTotal() ? 'bell-dot' : 'bell'" />
        </button>

        <!-- Panel -->
        <ng-template buiPopoverPortal>
          <div
            buiPopoverContent
            class="p-0"
          >
            <!-- Header -->
            <div class="mx-6 mt-5 flex items-center gap-x-1">
              <div class="text-xl font-medium">
                {{ t('LAYOUT.NOTIFICATIONS.TITLE') }}
              </div>

              <div class="flex-auto"></div>

              <button
                matIconButton
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.MARK_ALL_AS_READ')"
                [disabled]="!unreadTotal()"
                (click)="onMarkAllAsRead()"
              >
                <mat-icon svgIcon="check-check" />
              </button>
              <button
                matIconButton
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.DELETE_ALL')"
                [disabled]="!allTotal()"
                (click)="onDeleteAll()"
              >
                <mat-icon svgIcon="trash-2" />
              </button>
              <button
                matIconButton
                buiPopoverClose
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.CLOSE')"
              >
                <mat-icon svgIcon="x" />
              </button>
            </div>

            <!-- Filters -->
            <mat-tab-group
              class="mx-6 mt-2"
              [animationDuration]="0"
              [fitInkBarToContent]="true"
              [mat-stretch-tabs]="false"
              mat-align-tabs="start"
              [(selectedIndex)]="filter"
            >
              <mat-tab
                [label]="
                  t('LAYOUT.NOTIFICATIONS.TAB_ALL') +
                  (allTotal() ? ' (' + allTotal() + ')' : '')
                "
              />
              <mat-tab
                [label]="
                  t('LAYOUT.NOTIFICATIONS.TAB_UNREAD') +
                  (unreadTotal() ? ' (' + unreadTotal() + ')' : '')
                "
              />
              <mat-tab
                [label]="
                  t('LAYOUT.NOTIFICATIONS.TAB_FINANCE_INVOICE') +
                  (financeInvoiceTotal()
                    ? ' (' + financeInvoiceTotal() + ')'
                    : '')
                "
              />
            </mat-tab-group>

            <!-- Notifications -->
            <div
              class="flex min-h-88 flex-col gap-y-2 overflow-y-auto overscroll-contain px-6 pt-1 pb-4 sm:max-h-88"
            >
              @if (showSkeleton()) {
                <ng-template [buiSkeletonRepeat]="5">
                  <div class="flex w-full items-start gap-x-3 py-2">
                    <div
                      buiSkeleton
                      class="mt-1 size-6 shrink-0 rounded-lg"
                    ></div>
                    <div class="flex min-w-0 flex-auto flex-col gap-y-1.5">
                      <div
                        buiSkeleton
                        class="h-3.5 w-3/4"
                      ></div>
                      <div
                        buiSkeleton
                        class="h-3 w-full"
                      ></div>
                      <div
                        buiSkeleton
                        class="h-3 w-20"
                      ></div>
                    </div>
                  </div>
                </ng-template>
              } @else {
                @for (
                  notification of currentState().notifications;
                  track notification.id;
                  let last = $last
                ) {
                  <div class="flex w-full items-start gap-x-3 py-2">
                    <button
                      type="button"
                      buiPopoverClose
                      class="flex min-w-0 flex-auto items-start gap-x-3 text-start"
                      (click)="onNotificationClick(notification)"
                    >
                      <!-- Icon -->
                      <div
                        class="mt-1 flex size-6 shrink-0 items-center justify-center rounded-lg bg-neutral-a4 font-medium text-neutral-a11"
                      >
                        <mat-icon
                          [svgIcon]="iconFor(notification)"
                          class="size-3.5"
                        />
                      </div>

                      <div class="flex min-w-0 flex-col">
                        <!-- Title -->
                        <div class="flex items-center gap-x-2">
                          @if (!notification.readAt) {
                            <div
                              class="size-1 shrink-0 rounded-full bg-primary-9"
                              aria-hidden="true"
                            ></div>
                          }
                          <div
                            class="min-w-0 flex-auto truncate text-sm"
                            [class.font-semibold]="!notification.readAt"
                            [class.font-medium]="!!notification.readAt"
                          >
                            {{ notification.title }}
                          </div>
                        </div>

                        <!-- Message -->
                        @if (notification.message) {
                          <div class="text-xs">{{ notification.message }}</div>
                        }

                        <!-- Date -->
                        <div class="mt-0.5 text-xs text-neutral-a11">
                          {{
                            notification.createdAt
                              | translocoDate
                                : { dateStyle: 'short', timeStyle: 'short' }
                          }}
                        </div>
                      </div>
                    </button>

                    <!-- Delete -->
                    <button
                      matIconButton
                      class="small"
                      [matTooltip]="t('LAYOUT.NOTIFICATIONS.DELETE')"
                      (click)="onDelete(notification)"
                    >
                      <mat-icon svgIcon="trash-2" />
                    </button>
                  </div>

                  @if (!last) {
                    <mat-divider />
                  }
                } @empty {
                  <div class="py-6 text-center text-sm text-neutral-a11">
                    {{ t('LAYOUT.NOTIFICATIONS.EMPTY') }}
                  </div>
                }
              }

              @if (currentState().currentPage < currentState().lastPage) {
                <button
                  matButton
                  class="small tertiary mt-2 w-full"
                  (click)="onLoadMore()"
                  [showProgress]="currentState().loadingMore"
                >
                  <mat-progress-spinner
                    progressIndicator
                    mode="indeterminate"
                    diameter="16"
                    aria-label="Loading"
                    tabindex=""
                  />
                  {{ t('LAYOUT.NOTIFICATIONS.LOAD_MORE') }}
                </button>
              }
            </div>
          </div>
        </ng-template>
      </div>
    </ng-container>
  `,
})
export class Notifications {
  // Dependencies
  private readonly notificationsService = inject(NotificationsService);
  private readonly notificationsStreamService = inject(
    NotificationsStreamService
  );
  private readonly financeInvoiceService = inject(FinanceInvoiceService);
  private readonly router = inject(Router);

  // Inputs / Outputs
  readonly position = input<PopoverPosition>('bottom-end');

  // State
  protected readonly filter = signal<0 | 1 | 2>(0);

  // Computed state
  protected readonly currentTab = computed(() => TABS[this.filter()]);
  protected readonly currentState = computed(() =>
    this.notificationsService.states[this.currentTab()]()
  );
  protected readonly allTotal = computed(
    () => this.notificationsService.states.all().total
  );
  protected readonly unreadTotal = this.notificationsService.unreadCount;
  protected readonly financeInvoiceTotal = computed(
    () => this.notificationsService.states.financeInvoice().total
  );
  protected readonly showSkeleton = computed(() => {
    const state = this.currentState();
    return state.loading && state.notifications.length === 0;
  });

  protected onOpened() {
    this.notificationsService.reload();
    this.notificationsStreamService.ensurePermission();
  }

  protected onNotificationClick(notification: Notification) {
    if (notification.financeInvoiceId !== null) {
      this.financeInvoiceService.reload();
    }
    if (notification.hrefV2) {
      this.router.navigateByUrl(notification.hrefV2);
    }
    if (!notification.readAt) {
      this.notificationsService.markAsRead([notification.id]);
    }
  }

  protected onMarkAllAsRead() {
    this.notificationsService.markAllAsRead();
  }

  protected onDelete(notification: Notification) {
    this.notificationsService.delete([notification.id]);
  }

  protected onDeleteAll() {
    this.notificationsService.deleteAll();
  }

  protected onLoadMore() {
    this.notificationsService.loadMore(this.currentTab());
  }

  /**
   * Read off the link, not the tag: only some of the incident writers tag their
   * rows, but every one of them addresses the incident detail.
   */
  protected iconFor(notification: Notification): string {
    if (notification.hrefV2?.startsWith('/incidents/')) return 'triangle-alert';
    if (notification.hrefV2?.startsWith('/property-management-invoices/'))
      return 'receipt';
    return notification.financeInvoiceId !== null ? 'file-text' : 'bell';
  }
}

const TABS = [
  'all',
  'unread',
  'financeInvoice',
] as const satisfies readonly NotificationTab[];
